import { MATERIALS, type MaterialId } from './materials';
import { isMaterial, materialText } from '../core/packet';
import type { Packet, PacketMaterial } from '../core/packet';
import type { SinkEntity } from '../core/entities';
import type { SimState } from '../core/state';
import type { SubscriberColumn } from '../core/subscriber';
import { REPORT_METHOD_NAME } from './audit';
import type { AuditMethodName } from './audit';
import { DEPOSIT_METHOD_NAME, TreasuryService } from './treasury';
import type { TreasuryMethodName } from './treasury';

export type SellMethodName = `sell${Capitalize<MaterialId>}`;

/**
 * What the Subscriber's `next` column can be built from. Just the treasury: selling happens inside
 * the pipe now, so a packet that reaches a Subscriber is already proceeds.
 */
export type SubscriberNextMethodName = TreasuryMethodName;

/** What the `error` column can be built from — a failure is filed, never banked. */
export type SubscriberErrorMethodName = AuditMethodName;

export type SubscriberMethodName = SubscriberNextMethodName | SubscriberErrorMethodName;

export interface SubscriberService {
  name: string;
  methods: readonly SubscriberMethodName[];
}

/**
 * Which services each column of the Subscriber offers. A column takes one kind of notification only
 * — `next` a value, `error` a failure — so their menus have nothing in common, and this is the one
 * place that says so: the picker renders from it and `addSubscriberFunction` refuses anything
 * outside it. `complete` offers nothing yet, so the column reveals but stays unfillable.
 */
export const SUBSCRIBER_SERVICES: Record<SubscriberColumn, readonly SubscriberService[]> = {
  next: [{ name: 'TreasuryService', methods: [DEPOSIT_METHOD_NAME] }],
  error: [{ name: 'AuditService', methods: [REPORT_METHOD_NAME] }],
  complete: [],
};

/** False for a column that reveals but cannot yet be filled, so its slots never offer a click. */
export function columnOffersMethods(column: SubscriberColumn): boolean {
  return SUBSCRIBER_SERVICES[column].length > 0;
}

export function isMethodInstallableIn(
  column: SubscriberColumn,
  method: SubscriberMethodName,
): boolean {
  return SUBSCRIBER_SERVICES[column].some((service) => service.methods.includes(method));
}

export function sellMethodName(material: MaterialId): SellMethodName {
  return `sell${material.charAt(0).toUpperCase()}${material.slice(1)}` as SellMethodName;
}

export const SELL_METHOD_NAMES: readonly SellMethodName[] = (
  Object.keys(MATERIALS) as MaterialId[]
).map(sellMethodName);

/** How a method reads anywhere the player sees it — call parens included, so it shows as something
 * callable rather than a bare identifier. */
export function sellMethodLabel(name: SellMethodName | SubscriberMethodName): string {
  return `${name}()`;
}

/**
 * The same name broken over two lines for a map tile. `sellSolarPanel()` on one line has to shrink
 * past legibility to fit a single cell, whereas splitting after the `sell` prefix roughly halves
 * the widest line and buys back the font size.
 */
export function sellMethodTileLabel(name: SellMethodName | SubscriberMethodName): string {
  if (!name.startsWith('sell')) return `${name}()`;
  return `sell\n${name.slice('sell'.length)}()`;
}

/**
 * A sale is a request, not an assignment: it takes time to come back. That delay is the whole
 * reason a flattening operator has anything to decide — with an instant sale `switchMap`,
 * `concatMap` and `mergeMap` would be indistinguishable.
 */
export const SALE_SETTLE_BASE_TICKS = 4;

/** Dearer goods settle slower, so cancelling an in-flight sale costs most exactly where it hurts. */
export const SALE_SETTLE_DOLLARS_PER_TICK = 10;

export function saleSettleTicks(amount: number): number {
  return SALE_SETTLE_BASE_TICKS + Math.max(0, Math.ceil(amount / SALE_SETTLE_DOLLARS_PER_TICK));
}

export interface SaleQuote {
  material: MaterialId;
  amount: number;
  settleTicks: number;
}

/**
 * The inner Observable a sell method returns when the market is called from inside a pipe rather
 * than from the Subscriber — a sale that settles after a delay and emits its proceeds once.
 *
 * It banks nothing: it only prices, leaving the side effect to {@link TreasuryService} at the end of
 * the pipe. That is what makes it safe to put inside an operator, and it puts the throw for an
 * unsellable packet *upstream* of the Subscriber, which is the only place `catchError` can ever see
 * one.
 */
export function quoteSale(method: SellMethodName, material: PacketMaterial): SaleQuote {
  if (!isMaterial(material) || sellMethodName(material) !== method) {
    throw new Error(`${method} cannot sell a ${materialText(material)} packet`);
  }
  const amount = MATERIALS[material].sellPrice;
  return { material, amount, settleTicks: saleSettleTicks(amount) };
}

/**
 * Hands an arriving packet to the function the player installed in the Subscriber's `next` column.
 * Nothing dispatches any more — `deposit` is the only thing installable — but the seam stays,
 * because `next` is still a list the player builds and a packet it cannot take still has to throw.
 *
 * A packet that is not proceeds reaching here means no selling operator turned it into money, which
 * is a real error rather than a silent drop: `zip` with no `switchMap` behind it fails exactly where
 * RxJS would.
 */
export function applySubscriberFunction(
  state: SimState,
  sink: SinkEntity,
  packet: Packet,
  installed: readonly SubscriberNextMethodName[],
): number {
  return TreasuryService[installed[0]](state, sink, packet);
}
